import { Asset } from './asset.model';

/**
 * Asset Hierarchy aggregate root
 * Manages the complete asset structure and provides navigation methods
 */
export class AssetHierarchy {
  private readonly assetMap: Map<string, Asset>;

  constructor(private readonly roots: Asset[]) {
    this.assetMap = new Map();
    this.buildAssetMap(roots);
  }

  /**
   * Build internal map for fast lookup
   */
  private buildAssetMap(assets: Asset[]): void {
    for (const asset of assets) {
      this.assetMap.set(asset.id, asset);
      if (asset.hasChildren()) {
        this.buildAssetMap(Array.from(asset.children));
      }
    }
  }

  /**
   * Get all root assets (assets without parents)
   */
  getRoots(): ReadonlyArray<Asset> {
    return this.roots;
  }

  /**
   * Get all assets as flat list
   */
  getAllAssets(): Asset[] {
    return Array.from(this.assetMap.values());
  }

  /**
   * Find asset by ID
   */
  findById(id: string): Asset | undefined {
    return this.assetMap.get(id);
  }

  /**
   * Find assets by type
   */
  findByType(type: string): Asset[] {
    return this.getAllAssets().filter(
      (asset) => asset.type.toString() === type,
    );
  }

  /**
   * Find assets by name (case-insensitive partial match)
   */
  findByName(searchQuery: string): Asset[] {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return [];

    return this.getAllAssets().filter((asset) =>
      asset.name.toLowerCase().includes(query),
    );
  }

  /**
   * Get parent of an asset
   */
  getParent(asset: Asset): Asset | undefined {
    if (!asset.parentId) return undefined;
    return this.findById(asset.parentId);
  }

  /**
   * Get all ancestors of an asset (from parent to root)
   */
  getAncestors(asset: Asset): Asset[] {
    const ancestors: Asset[] = [];
    let current: Asset | undefined = asset;

    while (current?.parentId) {
      const parent = this.findById(current.parentId);
      if (!parent) break;
      ancestors.push(parent);
      current = parent;
    }

    return ancestors;
  }

  /**
   * Get path from root to asset
   */
  getPath(asset: Asset): Asset[] {
    const ancestors = this.getAncestors(asset);
    return [...ancestors.reverse(), asset];
  }

  /**
   * Get all descendants of an asset (recursive)
   */
  getDescendants(asset: Asset): Asset[] {
    const descendants: Asset[] = [];

    const collectDescendants = (current: Asset) => {
      for (const child of current.children) {
        descendants.push(child);
        collectDescendants(child);
      }
    };

    collectDescendants(asset);
    return descendants;
  }

  /**
   * Get siblings of an asset (assets with same parent)
   */
  getSiblings(asset: Asset): Asset[] {
    if (!asset.parentId) {
      // Root assets: siblings are other roots
      return this.roots.filter((root) => root.id !== asset.id);
    }

    const parent = this.findById(asset.parentId);
    if (!parent) return [];

    return Array.from(parent.children).filter((child) => child.id !== asset.id);
  }

  /**
   * Get depth of an asset in the hierarchy
   */
  getDepth(asset: Asset): number {
    return this.getAncestors(asset).length;
  }

  /**
   * Get maximum depth of the hierarchy
   */
  getMaxDepth(): number {
    let maxDepth = 0;

    for (const asset of this.getAllAssets()) {
      const depth = this.getDepth(asset);
      if (depth > maxDepth) {
        maxDepth = depth;
      }
    }

    return maxDepth;
  }

  /**
   * Get total number of assets
   */
  getTotalAssetCount(): number {
    return this.assetMap.size;
  }

  /**
   * Check if hierarchy contains asset
   */
  contains(assetId: string): boolean {
    return this.assetMap.has(assetId);
  }

  /**
   * Check if hierarchy is empty
   */
  isEmpty(): boolean {
    return this.roots.length === 0;
  }
}
